/**
 * Jewels By Vaidehee - Luxury Motion & Scroll Animation Engine
 * Ultra-lightweight native IntersectionObserver & 60fps RAF scroll engine.
 */
(function() {
  'use strict';

  // 1. Reading Scroll Progress Bar
  function initScrollProgress() {
    const progressBar = document.getElementById('jwl-scroll-progress');
    if (!progressBar) return;

    let ticking = false;
    function updateProgress() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      if (scrollHeight > 0) {
        const progress = Math.min(Math.max((scrollTop / scrollHeight) * 100, 0), 100);
        progressBar.style.width = progress + '%';
      }
      ticking = false;
    }

    window.addEventListener('scroll', function() {
      if (!ticking) {
        window.requestAnimationFrame(updateProgress);
        ticking = true;
      }
    }, { passive: true });

    updateProgress();
  }

  // 2. High-Performance Scroll Reveal Engine
  function initScrollReveal() {
    // Check for prefers-reduced-motion
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      // Fallback: make everything visible immediately
      document.querySelectorAll('.jwl-reveal, .jwl-reveal-left, .jwl-reveal-right').forEach(function(el) {
        el.classList.add('is-in-view');
      });
      return;
    }

    const observer = new IntersectionObserver(function(entries, obs) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in-view');
          obs.unobserve(entry.target);
        }
      });
    }, {
      root: null,
      rootMargin: '0px 0px -40px 0px',
      threshold: 0.08
    });

    const candidateSelectors = [
      '.section-title',
      '.cm-header',
      '.wpbingo-title',
      '.title-section',
      '.bwp-top-bar .title',
      '.product-card',
      '.metal-card',
      '.category-item',
      '.lookbook-item',
      '.bwp-image-description',
      '.ai-gen-block_cca7041',
      '.bwp-feature',
      '.wpbingo-section--testimonials',
      '.featured-collection-missjo'
    ];

    function observeElements() {
      const elements = document.querySelectorAll(candidateSelectors.join(', '));
      const windowHeight = window.innerHeight;

      elements.forEach(function(el) {
        if (el.classList.contains('jwl-observed')) return;
        el.classList.add('jwl-observed');

        // Check if inside header, marquee or top announcement (never animate these)
        if (el.closest('.header') || el.closest('#header-group') || el.closest('.jwl-luxury-marquee')) {
          return;
        }

        const rect = el.getBoundingClientRect();
        // If already in top viewport on initial page load, reveal immediately
        if (rect.top < windowHeight * 0.9 && rect.bottom > 0) {
          el.classList.add('jwl-reveal', 'is-in-view');
        } else {
          el.classList.add('jwl-reveal');
          observer.observe(el);
        }
      });
    }

    // Initial observation
    observeElements();

    // Re-check when dynamic content loads or mutations happen
    let mutationTimer = null;
    const mutationObserver = new MutationObserver(function() {
      if (mutationTimer) clearTimeout(mutationTimer);
      mutationTimer = setTimeout(observeElements, 150);
    });

    if (document.body) {
      mutationObserver.observe(document.body, { childList: true, subtree: true });
    }

    // Listen to theme custom events
    ['shopify:section:load', 'shopify:section:select', 'ajaxCompleted', 'facets:loaded'].forEach(function(evt) {
      document.addEventListener(evt, observeElements);
    });
  }

  // 3. Wishlist Heartbeat Trigger on Click
  function initWishlistMicroInteractions() {
    document.addEventListener('click', function(e) {
      const btn = e.target.closest('.btn-wishlist, [data-wishlist-handle]');
      if (btn) {
        btn.classList.add('active');
        btn.style.animation = 'none';
        void btn.offsetWidth; // trigger reflow
        btn.style.animation = 'jwlHeartbeat 0.9s cubic-bezier(0.215, 0.61, 0.355, 1) ease-in-out';
      }
    }, { passive: true });
  }

  // 4. Fly-to-Cart & Shopping Bag Bounce Engine
  function initFlyToCart() {
    function getVisibleCartTarget() {
      // Prioritize desktop if visible
      const desktopCart = document.querySelector('.header-desktop .header-cart__link, .bwp-header:not(.header-mobile) .header-cart__link');
      if (desktopCart && desktopCart.getBoundingClientRect().width > 0) {
        return desktopCart;
      }
      // Prioritize mobile if visible
      const mobileCart = document.querySelector('.header-mobile .header-cart__link');
      if (mobileCart && mobileCart.getBoundingClientRect().width > 0) {
        return mobileCart;
      }
      // General fallback
      return document.querySelector('.header-cart__link, .header-cart');
    }

    function triggerCartBounce() {
      const counts = document.querySelectorAll('.header-cart__count');
      const links = document.querySelectorAll('.header-cart__link');

      counts.forEach(function(badge) {
        badge.classList.remove('jwl-badge-bounce');
        void badge.offsetWidth;
        badge.classList.add('jwl-badge-bounce');
      });

      links.forEach(function(link) {
        link.classList.remove('jwl-cart-ripple');
        void link.offsetWidth;
        link.classList.add('jwl-cart-ripple');
      });
    }

    // Auto-trigger bounce whenever cart count badge changes
    const cartBadges = document.querySelectorAll('.header-cart__count');
    cartBadges.forEach(function(badge) {
      const observer = new MutationObserver(function() {
        triggerCartBounce();
      });
      observer.observe(badge, { childList: true, characterData: true, subtree: true });
    });

    // Intercept click on Add to Cart triggers
    document.addEventListener('click', function(e) {
      const btn = e.target.closest('button[type="submit"], [name="add"], .btn-addtocart, .product-form__submit, .add-to-cart, .btn-add-to-cart, .single_add_to_cart_button, .product-card__form, .add-group-to-cart');
      if (!btn) return;

      const form = btn.closest('form[action*="/cart/add"], form[data-product-form], .form-addtocart') || btn.closest('.product-card') || btn.closest('.product-single');
      if (!form && !btn.matches('[name="add"], .btn-addtocart')) return;

      // Find relevant product image
      let productImg = null;
      const card = btn.closest('.product-card');
      if (card) {
        productImg = card.querySelector('.product-card__image-wrapper img, .product-card__image img, img');
      } else {
        productImg = document.querySelector('.product-single__photos img, .product-media-main img, .photoswipe-item img, #featured-image, .featured-image img, .slick-current img, .swiper-slide-active img, .product-item img') || document.querySelector('.main-content img');
      }

      if (!productImg || !productImg.src) return;

      const cartTarget = getVisibleCartTarget();
      if (!cartTarget) return;

      const startRect = productImg.getBoundingClientRect();
      const targetRect = cartTarget.getBoundingClientRect();

      if (startRect.width === 0 || startRect.height === 0 || targetRect.width === 0) {
        triggerCartBounce();
        return;
      }

      // Create floating thumbnail
      const flyer = document.createElement('div');
      flyer.className = 'jwl-flying-product';
      const cloneImg = document.createElement('img');
      cloneImg.src = productImg.currentSrc || productImg.src;
      flyer.appendChild(cloneImg);

      const size = Math.min(Math.max(startRect.width, startRect.height), 120);
      const initialLeft = startRect.left + (startRect.width - size) / 2;
      const initialTop = startRect.top + (startRect.height - size) / 2;

      flyer.style.left = initialLeft + 'px';
      flyer.style.top = initialTop + 'px';
      flyer.style.width = size + 'px';
      flyer.style.height = size + 'px';
      flyer.style.opacity = '1';

      document.body.appendChild(flyer);

      const targetX = targetRect.left + targetRect.width / 2 - 14;
      const targetY = targetRect.top + targetRect.height / 2 - 14;

      if ('animate' in flyer) {
        const midX = (initialLeft + targetX) / 2;
        const midY = Math.min(initialTop, targetY) - 50;

        const animation = flyer.animate([
          {
            left: initialLeft + 'px',
            top: initialTop + 'px',
            width: size + 'px',
            height: size + 'px',
            opacity: 1,
            transform: 'scale(1) rotate(0deg)'
          },
          {
            left: midX + 'px',
            top: midY + 'px',
            width: (size * 0.6) + 'px',
            height: (size * 0.6) + 'px',
            opacity: 0.9,
            transform: 'scale(0.8) rotate(-10deg)',
            offset: 0.45
          },
          {
            left: targetX + 'px',
            top: targetY + 'px',
            width: '26px',
            height: '26px',
            opacity: 0.2,
            transform: 'scale(0.3) rotate(18deg)'
          }
        ], {
          duration: 720,
          easing: 'cubic-bezier(0.2, 0.85, 0.3, 1)'
        });

        animation.onfinish = function() {
          flyer.remove();
          triggerCartBounce();
        };
      } else {
        setTimeout(function() {
          flyer.remove();
          triggerCartBounce();
        }, 700);
      }
    }, true);
  }

  // Initialize all when DOM is interactive
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initScrollProgress();
      initScrollReveal();
      initWishlistMicroInteractions();
      initFlyToCart();
    });
  } else {
    initScrollProgress();
    initScrollReveal();
    initWishlistMicroInteractions();
    initFlyToCart();
  }
})();
